import { Typography, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ContractorBasicInformation from './ContractorBasicInformation'
import ScreenLoadingPlaceholder from '../../components/ScreenLoadingPlaceholder'
import { useAppDispatch } from '../../hooks/reduxHooks'
import { RouteConstants } from '../../navigation/navigation-types'
import { displayMessage } from '../../services/redux/snackbar/snackbar-actions'
import { ContractorDto } from '../../services/api/sharedTypes/Contractor'
import { ContractorManager } from '../../services/api/ContractorManager'
import { EventOfContractorDto } from '../../services/api/sharedTypes/Event'
import ContractorEventTable from '../../components/contractor/ContractorEventTable'
import { setSaveState } from '../../services/redux/savestate/savestate-actions'
import CustomContainer from '../../components/common/CustomContainer'
import BottomBar from '../../components/common/BottomBar'

const ContactDetail: React.FC = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const theme = useTheme()

    const [loadingData, setLoadingData] = useState<boolean>(true)
    const [isContractorSaving, setIsContractorSaving] = useState<boolean>(false)
    const [contractor, setContractor] = useState<ContractorDto>()
    const [oldContractor, setOldContractor] = useState<ContractorDto>()
    const [events, setEvents] = useState<EventOfContractorDto[]>()
    const [totalCosts, setTotalCosts] = useState<number>()

    if (!id)
        return (
            <Typography variant='h4' color='error' textAlign='center' marginY={theme.spacing(3)}>
                Tento pracovník neexistuje
            </Typography>
        )

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true)

            try {
                const contractorsRes = await ContractorManager.getManager().getContractorAsync(id)
                const eventRes = await ContractorManager.getManager().getEventsOfContractor(id)

                if (contractorsRes) {
                    setContractor(contractorsRes)
                    setOldContractor(contractorsRes)
                }
                if (eventRes) {
                    setEvents(eventRes.events)
                    setTotalCosts(eventRes.totalCosts)
                }
            } catch (error) {
                setLoadingData(false)
                dispatch(displayMessage({ message: error, type: 'error' }))
                return navigate(RouteConstants.workersAndCarriers)
            }

            setLoadingData(false)
        }

        fetchData()
    }, [setLoadingData])

    const saveContractor = async () => {
        if (!contractor) return

        setIsContractorSaving(true)

        try {
            const contractorRes = await ContractorManager.getManager().updateContractorAsync(id, contractor)
            if (contractorRes) {
                setContractor(contractorRes)
            }
        } catch (error) {
            setIsContractorSaving(false)
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        setIsContractorSaving(false)
        dispatch(displayMessage({ message: `Pracovník byl uložen`, type: 'success' }))
    }

    const deleteContractor = async () => {
        setIsContractorSaving(true)

        try {
            await ContractorManager.getManager().deleteContractorAsync(id)
        } catch (error) {
            setIsContractorSaving(false)
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        setIsContractorSaving(false)

        navigate(RouteConstants.workersAndCarriers)
        dispatch(displayMessage({ message: `Pracovník byl smazán`, type: 'success' }))
        dispatch(setSaveState(true))
    }

    const canGoBack =
        contractor?.name === oldContractor?.name &&
        contractor?.canBeAssigned === oldContractor?.canBeAssigned &&
        contractor?.emails.length === oldContractor?.emails.length &&
        contractor?.phones.length === oldContractor?.phones.length &&
        contractor?.lastName === oldContractor?.lastName &&
        contractor?.note === oldContractor?.note &&
        contractor?.type === oldContractor?.type &&
        contractor?.emails.every((email, index) => email === oldContractor?.emails[index]) &&
        contractor?.phones.every((phone, index) => phone === oldContractor?.phones[index])

    useEffect(() => {
        dispatch(setSaveState(canGoBack ?? true))
    }, [canGoBack, dispatch])

    if (loadingData) return <ScreenLoadingPlaceholder />

    if (!contractor)
        return (
            <Typography variant='h4' color='error' textAlign='center' marginY={theme.spacing(3)}>
                Tento pracovník neexistuje
            </Typography>
        )

    return (
        <CustomContainer>
            <ContractorBasicInformation contractor={contractor} setContractor={setContractor} />
            {events && <ContractorEventTable events={events} totalCosts={totalCosts ? totalCosts : 0} />}
            <BottomBar onDelete={deleteContractor} onSave={saveContractor} defaultRoute={RouteConstants.workersAndCarriers} disabled={isContractorSaving} deletedEntityName={contractor.name} />
        </CustomContainer>
    )
}

export default ContactDetail
